import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React, { forwardRef } from "react";
import { styles } from "./button-style";
import { Link } from "./link";

/* Our Button props: can either be a real button or a link, plus optional
 * style toggles (plain, outline, color).
 */
type ButtonProps = {
  className?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
  to?: string;
  plain?: boolean;
  outline?: boolean;
  color?: keyof typeof styles.colors;
  type?: "button" | "submit" | "reset";
};

/**
 * A "universal" button that either renders an <a> (via <Link>) or an actual
 * <button> (via <Headless.Button>) depending on whether a `to` prop is present.
 */
export const Button = forwardRef<HTMLElement, ButtonProps>(function Button({ to, plain, outline, color, className, children, ...props }, ref) {
  // Compute the final classes
  const classes = clsx(className, styles.base, outline ? styles.outline : plain ? styles.plain : clsx(styles.solid, styles.colors[color ?? "light/zinc"]));

  if (to) {
    // Render an <a> with React Router's <Link> if `to` is present
    return (
      <Link {...props} to={to} ref={ref as React.ForwardedRef<HTMLAnchorElement>} className={classes}>
        <TouchTarget>{children}</TouchTarget>
      </Link>
    );
  } else {
    // Otherwise, render a real <button>
    return (
      <Headless.Button {...props} ref={ref} className={classes}>
        <TouchTarget>{children}</TouchTarget>
      </Headless.Button>
    );
  }
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden" aria-hidden="true" />
      {children}
    </>
  );
}
